import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api, formatApiError } from "@/lib/api";
import { MapContainer, TileLayer, CircleMarker, Popup } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import { toast } from "sonner";
import {
  Sparkles, Globe2, TrendingUp, Layers, Flame, Brain, GitBranch, FileText,
  Lightbulb, Loader2, Plus, Command,
} from "lucide-react";

const TABS = [
  { key: "national", label: "National Overview", icon: Globe2 },
  { key: "provincial", label: "Provincial", icon: TrendingUp },
  { key: "investment", label: "Investment Intelligence", icon: Sparkles },
  { key: "utilisation", label: "Land Utilisation", icon: Layers },
  { key: "heatmaps", label: "Heat Maps", icon: Flame },
  { key: "forecasts", label: "Predictive Analytics", icon: Brain },
  { key: "scenarios", label: "Scenario Modelling", icon: GitBranch },
  { key: "briefings", label: "Executive Briefings", icon: FileText },
  { key: "spatial-ai", label: "Spatial AI", icon: Lightbulb },
];

const KPI = ({ label, value, hint, testid }) => (
  <div className="cad-panel p-4" data-testid={testid}>
    <div className="font-mono-cad text-[10px] uppercase tracking-[0.22em] text-[#7C93A5]">{label}</div>
    <div className="cad-kpi-value text-3xl mt-1 text-[#F6F8FA]">{value}</div>
    {hint && <div className="text-[11px] text-[#B3C2CC] mt-1 font-mono-cad">{hint}</div>}
  </div>
);

const HEAT_COLOR = {
  investment: "#C99A2E", mining_density: "#E67E7E", survey_activity: "#7CBFDC",
  claim_disputes: "#E67E7E", illegal_occupation: "#E67E7E",
  environmental_sensitivity: "#6BB88A", development_pressure: "#C99A2E",
  infrastructure_availability: "#7CBFDC", revenue_generation: "#6BB88A",
};

export const EnterpriseIntelligence = () => {
  const qc = useQueryClient();
  const [tab, setTab] = useState("national");
  const [heatType, setHeatType] = useState("investment");
  const [briefingCadence, setBriefingCadence] = useState("weekly");
  const [scenario, setScenario] = useState({ name: "", kind: "infrastructure" });

  const ref = useQuery({ queryKey: ["intel-ref"], queryFn: async () => (await api.get("/cadastre360/intelligence/reference")).data });
  const overview = useQuery({ queryKey: ["intel-overview"], queryFn: async () => (await api.get("/cadastre360/intelligence/national-overview")).data });
  const provincial = useQuery({ queryKey: ["intel-provincial"], queryFn: async () => (await api.get("/cadastre360/intelligence/provincial")).data });
  const investment = useQuery({ queryKey: ["intel-investment"], queryFn: async () => (await api.get("/cadastre360/intelligence/investment")).data });
  const utilisation = useQuery({ queryKey: ["intel-util"], queryFn: async () => (await api.get("/cadastre360/intelligence/land-utilisation")).data });
  const heatTypes = useQuery({ queryKey: ["intel-heatmaps"], queryFn: async () => (await api.get("/cadastre360/intelligence/heatmaps")).data });
  const heatCells = useQuery({
    queryKey: ["intel-heat", heatType],
    queryFn: async () => (await api.get(`/cadastre360/intelligence/heatmaps/${heatType}`)).data,
    enabled: tab === "heatmaps",
  });
  const forecasts = useQuery({ queryKey: ["intel-fc"], queryFn: async () => (await api.get("/cadastre360/intelligence/forecasts")).data });
  const scenarios = useQuery({ queryKey: ["intel-sc"], queryFn: async () => (await api.get("/cadastre360/intelligence/scenarios")).data });
  const briefings = useQuery({
    queryKey: ["intel-br", briefingCadence],
    queryFn: async () => (await api.get("/cadastre360/intelligence/briefings", { params: { cadence: briefingCadence || undefined }})).data,
  });
  const spatialAI = useQuery({ queryKey: ["intel-sa"], queryFn: async () => (await api.get("/cadastre360/intelligence/spatial-ai")).data });

  const runScenario = useMutation({
    mutationFn: async () => (await api.post("/cadastre360/intelligence/scenarios", scenario)).data,
    onSuccess: (d) => {
      toast.success(`Scenario evaluated: ${d.impact_summary.split(";")[0]}`);
      qc.invalidateQueries({ queryKey: ["intel-sc"] });
      setScenario({ name: "", kind: scenario.kind });
    },
    onError: (e) => toast.error(formatApiError(e)),
  });

  const generateBriefing = useMutation({
    mutationFn: async () => (await api.post("/cadastre360/intelligence/briefings", { cadence: briefingCadence })).data,
    onSuccess: () => {
      toast.success("Fresh executive briefing generated");
      qc.invalidateQueries({ queryKey: ["intel-br", briefingCadence] });
    },
    onError: (e) => toast.error(formatApiError(e)),
  });

  const k = overview.data?.kpis;

  return (
    <div className="space-y-6" data-testid="page-enterprise-intelligence">
      <header>
        <div className="font-mono-cad text-[10px] uppercase tracking-[0.28em] text-[#7C93A5]">
          Cadastre360™ — Enterprise Land Intelligence & National Spatial Analytics
        </div>
        <h1 className="font-display text-3xl font-bold text-[#F6F8FA] mt-1 flex items-center gap-2">
          <Command className="text-[#C99A2E]" /> Enterprise Land Intelligence
        </h1>
        <div className="text-[13px] text-[#B3C2CC] mt-1 max-w-4xl leading-relaxed">
          National executive decision-support platform. All dashboards are read-only aggregations that never modify production records.
          Existing dashboards, Digital Twin and Migration modules remain unchanged.
        </div>
      </header>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        <KPI label="Total Parcels" value={(k?.total_parcels ?? 0).toLocaleString()} testid="ei-kpi-parcels" />
        <KPI label="Mineral Rights" value={(k?.total_mineral_rights ?? 0).toLocaleString()}
             hint={`${k?.active_mineral_rights ?? 0} active`} testid="ei-kpi-rights" />
        <KPI label="Applications" value={(k?.applications ?? 0).toLocaleString()}
             hint={`${k?.approvals ?? 0} approvals`} testid="ei-kpi-apps" />
        <KPI label="Surveys Pending" value={(k?.surveys_pending ?? 0).toLocaleString()} testid="ei-kpi-surveys" />
        <KPI label="Mining Migration" value={(k?.mining_migration_committed ?? 0).toLocaleString()} hint="committed" testid="ei-kpi-mm" />
        <KPI label="Land Migration" value={(k?.land_migration_published ?? 0).toLocaleString()} hint={`${k?.land_migration_batches ?? 0} batches`} testid="ei-kpi-lm" />
      </div>

      <div className="cad-panel px-2 py-2 flex flex-wrap gap-1" data-testid="ei-tabs">
        {TABS.map((t) => (
          <button key={t.key} onClick={() => setTab(t.key)} data-testid={`ei-tab-${t.key}`}
            className={`px-3 py-2 rounded-sm text-[11.5px] font-mono-cad uppercase tracking-[0.14em] inline-flex items-center gap-1.5
                        ${tab === t.key ? "bg-[#145A7A]/60 text-[#F6F8FA]" : "text-[#B3C2CC] hover:bg-[#145A7A]/25"}`}>
            <t.icon size={12} className="text-[#C99A2E]" /> {t.label}
          </button>
        ))}
      </div>

      {/* NATIONAL */}
      {tab === "national" && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="ei-national">
          <div className="cad-panel p-5">
            <div className="font-mono-cad text-[10px] uppercase tracking-[0.24em] text-[#C99A2E] mb-3">Parcels by province</div>
            <table className="w-full text-[12.5px]">
              <thead><tr className="text-left border-b cad-hairline">
                {["Province", "Parcels", "Mineral rights"].map((h) => (
                  <th key={h} className="px-2 py-1.5 font-mono-cad text-[10px] uppercase tracking-[0.16em] text-[#7C93A5]">{h}</th>
                ))}
              </tr></thead>
              <tbody className="cad-tbody">
                {(overview.data?.parcels_by_province || []).map((p, i) => (
                  <tr key={p.province} data-testid={`ei-national-${p.province.replace(/\s+/g, "-")}`}>
                    <td className="px-2 py-1.5 text-[#F6F8FA]">{p.province}</td>
                    <td className="px-2 py-1.5 font-mono-cad">{p.count.toLocaleString()}</td>
                    <td className="px-2 py-1.5 font-mono-cad text-[#C99A2E]">{overview.data?.rights_by_province?.[i]?.count?.toLocaleString() || 0}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="cad-panel p-5">
            <div className="font-mono-cad text-[10px] uppercase tracking-[0.24em] text-[#C99A2E] mb-3">AI executive summary</div>
            <ul className="space-y-2">
              {(overview.data?.ai_summary || []).map((s, i) => (
                <li key={i} className="flex items-start gap-2 text-[13px] text-[#F6F8FA] leading-relaxed">
                  <Sparkles size={13} className="text-[#C99A2E] mt-1" />{s}
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}

      {/* PROVINCIAL */}
      {tab === "provincial" && (
        <div className="cad-panel p-5" data-testid="ei-provincial">
          <div className="font-mono-cad text-[10px] uppercase tracking-[0.24em] text-[#C99A2E] mb-3">Provincial comparison</div>
          <div className="overflow-x-auto">
            <table className="w-full text-[12px] min-w-[900px]">
              <thead><tr className="text-left border-b cad-hairline">
                {["Province", "Parcels", "Rights", "Active", "Dormant", "Apps", "Approval %", "Backlog", "Env Risk", "Invest Idx", "Road", "Rail", "Power", "Water"].map((h) => (
                  <th key={h} className="px-2 py-1.5 font-mono-cad text-[10px] uppercase tracking-[0.14em] text-[#7C93A5]">{h}</th>
                ))}
              </tr></thead>
              <tbody className="cad-tbody">
                {(provincial.data?.provinces || []).map((r) => (
                  <tr key={r.province} data-testid={`ei-prov-${r.province.replace(/\s+/g, "-")}`}>
                    <td className="px-2 py-1.5 text-[#F6F8FA]">{r.province}</td>
                    <td className="px-2 py-1.5 font-mono-cad">{r.parcels.toLocaleString()}</td>
                    <td className="px-2 py-1.5 font-mono-cad">{r.mineral_rights}</td>
                    <td className="px-2 py-1.5 font-mono-cad text-[#6BB88A]">{r.active_rights}</td>
                    <td className="px-2 py-1.5 font-mono-cad text-[#E67E7E]">{r.dormant_rights}</td>
                    <td className="px-2 py-1.5 font-mono-cad">{r.applications}</td>
                    <td className="px-2 py-1.5 font-mono-cad text-[#C99A2E]">{r.approval_performance_pct}%</td>
                    <td className="px-2 py-1.5 font-mono-cad">{r.survey_backlog}</td>
                    <td className="px-2 py-1.5 font-mono-cad">{r.environmental_risk_index}</td>
                    <td className="px-2 py-1.5 font-mono-cad text-[#C99A2E]">{r.investment_activity_index}</td>
                    <td className="px-2 py-1.5 font-mono-cad">{r.infrastructure?.road}</td>
                    <td className="px-2 py-1.5 font-mono-cad">{r.infrastructure?.rail}</td>
                    <td className="px-2 py-1.5 font-mono-cad">{r.infrastructure?.power}</td>
                    <td className="px-2 py-1.5 font-mono-cad">{r.infrastructure?.water}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* INVESTMENT */}
      {tab === "investment" && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4" data-testid="ei-investment">
          <div className="cad-panel p-5 lg:col-span-2">
            <div className="font-mono-cad text-[10px] uppercase tracking-[0.24em] text-[#C99A2E]">Top investment corridors</div>
            <table className="w-full mt-3 text-[12.5px]">
              <thead><tr className="text-left border-b cad-hairline">
                {["Corridor", "Province", "Commodity", "Score", "Value $M", "Unclaimed ha", "Readiness"].map((h) => (
                  <th key={h} className="px-2 py-1.5 font-mono-cad text-[10px] uppercase tracking-[0.16em] text-[#7C93A5]">{h}</th>
                ))}
              </tr></thead>
              <tbody className="cad-tbody">
                {(investment.data?.zones || []).slice(0, 16).map((z) => (
                  <tr key={z.id} data-testid={`ei-zone-${z.id}`}>
                    <td className="px-2 py-1.5 text-[#F6F8FA]">{z.name}</td>
                    <td className="px-2 py-1.5 text-[#B3C2CC]">{z.province}</td>
                    <td className="px-2 py-1.5 text-[#B3C2CC]">{z.commodity}</td>
                    <td className="px-2 py-1.5 font-mono-cad text-[#C99A2E]">{z.opportunity_score}</td>
                    <td className="px-2 py-1.5 font-mono-cad">${z.estimated_value_usd_m}M</td>
                    <td className="px-2 py-1.5 font-mono-cad">{z.unclaimed_area_ha.toLocaleString()}</td>
                    <td className="px-2 py-1.5"><span className="cad-chip cad-chip-gold text-[9px] uppercase">{z.development_readiness}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="cad-panel p-5">
            <div className="font-mono-cad text-[10px] uppercase tracking-[0.24em] text-[#C99A2E]">Portfolio totals</div>
            <div className="mt-3 space-y-3">
              <div><div className="font-mono-cad text-[10px] uppercase text-[#7C93A5]">Estimated value</div>
                <div className="text-2xl font-display font-bold text-[#F6F8FA]">${(investment.data?.totals?.total_estimated_value_usd_m ?? 0).toLocaleString()}M</div></div>
              <div><div className="font-mono-cad text-[10px] uppercase text-[#7C93A5]">Unclaimed land</div>
                <div className="text-2xl font-display font-bold text-[#F6F8FA]">{(investment.data?.totals?.total_unclaimed_ha ?? 0).toLocaleString()} ha</div></div>
              <div><div className="font-mono-cad text-[10px] uppercase text-[#7C93A5]">Protected area conflicts</div>
                <div className="text-2xl font-display font-bold text-[#E67E7E]">{investment.data?.totals?.protected_area_conflicts ?? 0}</div></div>
            </div>
            <div className="border-t cad-hairline mt-3 pt-3">
              <div className="font-mono-cad text-[10px] uppercase text-[#C99A2E]">AI recommendations</div>
              <ul className="mt-2 space-y-1.5 text-[12.5px] text-[#DCE3E8]">
                {(investment.data?.ai_recommendations || []).map((r, i) => (
                  <li key={i} className="flex items-start gap-1.5"><Sparkles size={11} className="text-[#C99A2E] mt-1" />{r}</li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      )}

      {/* UTILISATION */}
      {tab === "utilisation" && (
        <div className="cad-panel p-5" data-testid="ei-utilisation">
          <div className="font-mono-cad text-[10px] uppercase tracking-[0.24em] text-[#C99A2E]">National land utilisation</div>
          <div className="mt-3 space-y-2">
            {(utilisation.data?.rows || []).map((r) => (
              <div key={r.land_use} data-testid={`ei-util-${r.land_use}`}>
                <div className="flex items-center justify-between text-[12.5px]">
                  <span className="text-[#F6F8FA]">{r.land_use}</span>
                  <span className="font-mono-cad text-[#C99A2E]">{r.count.toLocaleString()} · {r.pct}%</span>
                </div>
                <div className="h-2 bg-[#18232D] rounded-full overflow-hidden mt-1">
                  <div className="h-full bg-[#C99A2E]" style={{ width: `${Math.min(100, r.pct * 2)}%` }} />
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* HEATMAPS */}
      {tab === "heatmaps" && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4" data-testid="ei-heatmaps">
          <div className="cad-panel p-5">
            <div className="font-mono-cad text-[10px] uppercase tracking-[0.24em] text-[#C99A2E]">Heat map types</div>
            <div className="space-y-1 mt-3">
              {(heatTypes.data?.types || []).map((t) => (
                <button key={t.type} onClick={() => setHeatType(t.type)} data-testid={`ei-heat-btn-${t.type}`}
                  className={`w-full text-left px-3 py-2 rounded-sm text-[12px] flex justify-between items-center
                              ${heatType === t.type ? "bg-[#145A7A]/60 text-[#F6F8FA]" : "text-[#B3C2CC] hover:bg-[#145A7A]/25"}`}>
                  <span>{t.label}</span>
                  <span className="font-mono-cad text-[10px] text-[#C99A2E]">{t.cells}</span>
                </button>
              ))}
            </div>
          </div>
          <div className="cad-panel p-1 lg:col-span-2" style={{ height: 520 }} data-testid="ei-heat-map">
            <MapContainer center={[-19, 30]} zoom={5.6} style={{ height: "100%", width: "100%" }} scrollWheelZoom>
              <TileLayer attribution="© OpenStreetMap" url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
              {(heatCells.data?.cells || []).map((c) => (
                <CircleMarker key={c.id} center={[c.lat, c.lon]}
                  radius={4 + c.intensity * 10}
                  pathOptions={{
                    color: HEAT_COLOR[heatType] || "#C99A2E",
                    fillColor: HEAT_COLOR[heatType] || "#C99A2E",
                    fillOpacity: 0.55 * c.intensity + 0.15,
                    weight: 0.5,
                  }}>
                  <Popup>
                    <div className="text-[12px]"><b>{heatType.replace(/_/g, " ")}</b><br />
                    {c.province} · intensity {Math.round(c.intensity * 100)}%</div>
                  </Popup>
                </CircleMarker>
              ))}
            </MapContainer>
          </div>
        </div>
      )}

      {/* FORECASTS */}
      {tab === "forecasts" && (
        <div className="cad-panel p-5" data-testid="ei-forecasts">
          <div className="font-mono-cad text-[10px] uppercase tracking-[0.24em] text-[#C99A2E]">12-month predictive forecasts</div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 mt-3">
            {(forecasts.data?.forecasts || []).map((f) => {
              const values = f.series.map((s) => s.projected);
              const min = Math.min(...values), max = Math.max(...values), rng = max - min || 1;
              return (
                <div key={f.id} className="border cad-hairline rounded-sm p-3 bg-[#071B2B]" data-testid={`ei-fc-${f.metric}`}>
                  <div className="flex items-center justify-between">
                    <div className="text-[#F6F8FA] font-display font-bold text-[13px]">{f.metric.replace(/_/g, " ")}</div>
                    <span className="cad-chip cad-chip-gold text-[9px] uppercase">conf {Math.round(f.confidence * 100)}%</span>
                  </div>
                  <div className="flex items-end gap-0.5 mt-2" style={{ height: 40 }}>
                    {values.map((v, i) => (
                      <div key={i} className="flex-1 bg-[#C99A2E]/60 rounded-sm"
                           style={{ height: `${((v - min) / rng) * 100 + 6}%` }} />
                    ))}
                  </div>
                  <div className="text-[11px] font-mono-cad text-[#B3C2CC] mt-1">
                    {values[0]?.toLocaleString()} → {values[values.length - 1]?.toLocaleString()} {f.unit}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* SCENARIOS */}
      {tab === "scenarios" && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4" data-testid="ei-scenarios">
          <div className="cad-panel p-5">
            <div className="font-mono-cad text-[10px] uppercase tracking-[0.24em] text-[#C99A2E]">Model a scenario</div>
            <div className="text-[12px] text-[#B3C2CC] mt-1">Simulations run independently and never modify production data.</div>
            <div className="space-y-2 mt-3">
              <input value={scenario.name} onChange={(e) => setScenario({ ...scenario, name: e.target.value })}
                     placeholder="Scenario name" data-testid="ei-sc-name"
                     className="w-full bg-[#071B2B] border cad-hairline rounded-sm px-2 py-2 text-[13px] text-[#F6F8FA]" />
              <select value={scenario.kind} onChange={(e) => setScenario({ ...scenario, kind: e.target.value })}
                      data-testid="ei-sc-kind"
                      className="w-full bg-[#071B2B] border cad-hairline rounded-sm px-2 py-2 text-[13px] text-[#F6F8FA]">
                <option value="infrastructure">Infrastructure expansion</option>
                <option value="environment">Environmental / protected area</option>
                <option value="policy">Policy change</option>
                <option value="mining">Mining licence adjustment</option>
              </select>
              <button onClick={() => runScenario.mutate()} disabled={!scenario.name || runScenario.isPending}
                      data-testid="ei-sc-run"
                      className="cad-btn-primary w-full py-2 rounded-sm text-[13px] inline-flex items-center justify-center gap-1.5">
                {runScenario.isPending ? <Loader2 size={13} className="animate-spin" /> : <GitBranch size={13} />} Run simulation
              </button>
            </div>
          </div>
          <div className="cad-panel p-5 lg:col-span-2">
            <div className="font-mono-cad text-[10px] uppercase tracking-[0.24em] text-[#C99A2E]">Scenario history</div>
            <table className="w-full mt-3 text-[12.5px]">
              <thead><tr className="text-left border-b cad-hairline">
                {["Scenario", "Kind", "Impact summary", "Created"].map((h) => (
                  <th key={h} className="px-2 py-1.5 font-mono-cad text-[10px] uppercase tracking-[0.16em] text-[#7C93A5]">{h}</th>
                ))}
              </tr></thead>
              <tbody className="cad-tbody">
                {(scenarios.data?.scenarios || []).map((s) => (
                  <tr key={s.id} data-testid={`ei-sc-${s.id}`}>
                    <td className="px-2 py-1.5 text-[#F6F8FA]">{s.name}</td>
                    <td className="px-2 py-1.5"><span className="cad-chip cad-chip-blue text-[9px] uppercase">{s.kind}</span></td>
                    <td className="px-2 py-1.5 text-[#DCE3E8] text-[11px]">{s.impact_summary}</td>
                    <td className="px-2 py-1.5 font-mono-cad text-[10px] text-[#B3C2CC]">{s.created_at?.slice(0, 16).replace("T", " ")}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* BRIEFINGS */}
      {tab === "briefings" && (
        <div className="space-y-4" data-testid="ei-briefings">
          <div className="cad-panel p-4 flex flex-wrap items-center gap-2">
            <span className="font-mono-cad text-[10px] uppercase tracking-[0.22em] text-[#7C93A5]">Cadence:</span>
            {(ref.data?.briefing_cadences || []).map((c) => (
              <button key={c} onClick={() => setBriefingCadence(c)} data-testid={`ei-br-${c}`}
                className={`px-3 py-1 rounded-sm text-[11px] font-mono-cad uppercase tracking-[0.14em]
                            ${briefingCadence === c ? "bg-[#145A7A]/60 text-[#F6F8FA]" : "text-[#B3C2CC] hover:bg-[#145A7A]/25"}`}>
                {c}
              </button>
            ))}
            <button onClick={() => generateBriefing.mutate()} disabled={generateBriefing.isPending}
                    data-testid="ei-br-generate"
                    className="cad-btn-primary ml-auto px-3 py-1.5 text-[11px] rounded-sm inline-flex items-center gap-1">
              {generateBriefing.isPending ? <Loader2 size={11} className="animate-spin" /> : <Plus size={11} />} Generate new briefing
            </button>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {(briefings.data?.briefings || []).map((b) => (
              <div key={b.id} className="cad-panel p-5" data-testid={`ei-briefing-${b.id}`}>
                <div className="flex items-center justify-between">
                  <div>
                    <div className="font-mono-cad text-[10px] uppercase tracking-[0.24em] text-[#C99A2E]">{b.cadence}</div>
                    <div className="font-display font-bold text-[#F6F8FA] text-[16px] mt-1">{b.title}</div>
                  </div>
                  <div className="text-[10px] font-mono-cad text-[#B3C2CC]">{b.generated_at?.slice(0, 10)}</div>
                </div>
                <div className="mt-3">
                  <div className="font-mono-cad text-[10px] uppercase text-[#7C93A5]">Highlights</div>
                  <ul className="mt-1 space-y-1 text-[12.5px] text-[#F6F8FA]">
                    {(b.highlights || []).map((h, i) => (<li key={i} className="flex items-start gap-1.5"><Sparkles size={11} className="text-[#C99A2E] mt-1" />{h}</li>))}
                  </ul>
                </div>
                <div className="mt-3">
                  <div className="font-mono-cad text-[10px] uppercase text-[#E67E7E]">Risks</div>
                  <ul className="mt-1 space-y-1 text-[12.5px] text-[#DCE3E8]">
                    {(b.risks || []).map((h, i) => (<li key={i}>· {h}</li>))}
                  </ul>
                </div>
                <div className="mt-3">
                  <div className="font-mono-cad text-[10px] uppercase text-[#6BB88A]">Recommendations</div>
                  <ul className="mt-1 space-y-1 text-[12.5px] text-[#DCE3E8]">
                    {(b.recommendations || []).map((h, i) => (<li key={i}>· {h}</li>))}
                  </ul>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* SPATIAL AI */}
      {tab === "spatial-ai" && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4" data-testid="ei-spatial-ai">
          <div className="cad-panel p-5">
            <div className="font-mono-cad text-[10px] uppercase tracking-[0.24em] text-[#C99A2E]">Finding types</div>
            <div className="space-y-2 mt-3">
              {(spatialAI.data?.by_type || []).map((t) => (
                <div key={t.type} className="flex items-center justify-between border cad-hairline rounded-sm px-3 py-2 bg-[#071B2B]" data-testid={`ei-sa-type-${t.type}`}>
                  <span className="text-[#F6F8FA] text-[12px]">{t.label}</span>
                  <span className="cad-chip cad-chip-gold text-[10px]">{t.count}</span>
                </div>
              ))}
            </div>
          </div>
          <div className="cad-panel p-5 lg:col-span-2">
            <div className="font-mono-cad text-[10px] uppercase tracking-[0.24em] text-[#C99A2E]">Latest findings</div>
            <table className="w-full mt-3 text-[12px]">
              <thead><tr className="text-left border-b cad-hairline">
                {["Type", "Province", "Severity", "Confidence", "Note"].map((h) => (
                  <th key={h} className="px-2 py-1.5 font-mono-cad text-[10px] uppercase tracking-[0.16em] text-[#7C93A5]">{h}</th>
                ))}
              </tr></thead>
              <tbody className="cad-tbody">
                {(spatialAI.data?.findings || []).slice(0, 30).map((f) => (
                  <tr key={f.id} data-testid={`ei-sa-${f.id}`}>
                    <td className="px-2 py-1.5 text-[#F6F8FA]">{f.type.replace(/_/g, " ")}</td>
                    <td className="px-2 py-1.5 text-[#B3C2CC]">{f.province}</td>
                    <td className="px-2 py-1.5"><span className={`cad-chip ${f.severity === "critical" ? "cad-chip-red" : f.severity === "high" ? "cad-chip-red" : f.severity === "moderate" ? "cad-chip-gold" : "cad-chip-blue"} text-[9px] uppercase`}>{f.severity}</span></td>
                    <td className="px-2 py-1.5 font-mono-cad text-[#C99A2E]">{Math.round(f.confidence * 100)}%</td>
                    <td className="px-2 py-1.5 text-[#DCE3E8] text-[11px]">{f.note}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div className="text-[10px] font-mono-cad uppercase tracking-widest text-[#5F7688]">
        Read-only executive intelligence — refresh {overview.data?.generated_at?.slice(0, 19)}
      </div>
    </div>
  );
};
